import { Box, Flex, Input, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FileInput } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { FormSelect } from '../../../components/Select'
import VideoFileInput from '../../../components/VideoFileInput/VideoFileInput'
import { useAppToast } from '../../../helpers'
import { IOurStudentFormFields, ITestimonialsFormModalProps } from '../../../interfaces/Testimonials/ITestimonials'
import { TestimonialsService } from '../../../services/Testimonials/TestimonialsService'

// OurStudentStoreRequest: `image => image|mimes:jpeg,jpg,png,gif,webp|max:2048`.
export const TESTIMONIAL_IMAGE_MAX_MB = 2

const baseForm: IOurStudentFormFields = {
  name: '',
  knowledge_area_id: 0,
  description: '',
  video: undefined,
  image: undefined
}

const baseFormErrors = {
  name: '',
  knowledge_area_id: '',
  description: '',
  video: '',
  image: ''
}

export function TestimonialsFormModal({
  isOpen,
  onClose,
  ourStudent,
  knowledgeAreas,
  onSuccess
}: ITestimonialsFormModalProps) {
  const service = new TestimonialsService()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<IOurStudentFormFields>(baseForm)
  const [formErrors, setFormErrors] = useState(baseFormErrors)

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)
      setForm(
        ourStudent
          ? {
              name: ourStudent.name,
              knowledge_area_id: ourStudent.knowledge_area_id,
              description: ourStudent.description,
              video: undefined,
              image: undefined
            }
          : baseForm
      )
    }
  }, [isOpen, ourStudent])

  const handleSave = async () => {
    setSaving(true)
    service
      .create(form)
      .then(() => {
        toast.success('Registro salvo com sucesso')
        onClose()
        onSuccess?.()
      })
      .catch((response) => {
        if (response.response?.status === 422) {
          setFormErrors(response.response.data.errors)
          return
        }

        toast.error('Erro ao adicionar estudante')
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${ourStudent ? 'Editar' : 'Registrar'} Estudante`}
      onSubmit={handleSave}
      size="wide"
      isLoading={saving}
      isConfirmDisabled={saving}
    >
      <Flex direction="column" gap={3}>
        <Box>
          <FormField label="Nome" htmlFor="name" error={formErrors.name}>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={saving}
            />
          </FormField>
        </Box>

        <Box>
          <FormField label="Área de Conhecimento" htmlFor="knowledge_area_id" error={formErrors.knowledge_area_id}>
            <FormSelect
              id="knowledge_area_id"
              isDisabled={saving}
              value={form.knowledge_area_id}
              onChange={(value) => setForm({ ...form, knowledge_area_id: parseInt(value) })}
              options={knowledgeAreas.map((area) => ({ value: area.id, label: area.name }))}
              placeholder="Selecione uma área"
              emptyValue={0}
            />
          </FormField>
        </Box>

        <Box>
          <FormField label="Depoimento" htmlFor="description" error={formErrors.description}>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={saving}
            />
          </FormField>
        </Box>

        <VideoFileInput
          label="Vídeo do depoimento"
          helperText="Arquivo MP4, até 100MB. Prefira enviar já em 720p — o site serve o arquivo como veio."
          file={form.video ?? null}
          onChange={(file) => setForm({ ...form, video: file ?? undefined })}
          isDisabled={saving}
          allowRemoveExisting={false}
          error={formErrors.video ? [formErrors.video].flat() : undefined}
        />

        <FileInput
          accept="image"
          maxSizeMB={TESTIMONIAL_IMAGE_MAX_MB}
          label="Imagem"
          value={form.image ?? null}
          onChange={(image) => setForm({ ...form, image: image ?? undefined })}
          error={formErrors.image}
          isDisabled={saving}
        />
      </Flex>
    </FormModal>
  )
}
